import type { Priority } from "@app/core";
import type { CSSProperties } from "react";
import { pastel, PRIORITY_FLAG_HEX } from "../task-colors.js";

const EVENT_DEFAULT = "#64748b";
const NEUTRAL_INK = "#334155";
// Softening levels mirror the create sheet's wash (SheetWash.tsx): the topic
// band ≈ 0.5, the custom/calendar fill ≈ 0.58 — so a block reads in the same
// color the sheet showed when the entry was created.
const SOFT_TOPIC = 0.5;
const SOFT_OTHER = 0.58;

export interface MobileBlockInput {
  priority: Priority;
  /** Continuous matrix-diagonal priority color (`useMatrixPriorityColor`) —
   *  non-null only for a matrix-PLACED task in scope under the matrix
   *  presentation. Null ⇒ the border/glow uses the discrete flag hue. */
  matrixColor?: string | null;
  customColor: string | null;
  /** Colors of the task's topics, in order (rail-fallback resolved, so colorless
   *  topics still carry a color — empty only when the task has no topics). */
  topicColors: string[];
  calendarColor: string | null;
  isEvent?: boolean;
}

export interface MobileBlockStyle {
  /** Block wrapper: the picked-color fill (solid / topic gradient) + priority
   *  border/outward glow. */
  container: CSSProperties;
  /** True when a color fills the body (text then uses readable solids). */
  filled: boolean;
  /** Title text span color. */
  titleText: CSSProperties;
  /** Per-section text colors (time/location/notes). */
  metaColors: { time: string; location: string; notes: string };
}

function readable(hex: string): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return "#1f2937";
  const n = parseInt(m[1]!, 16);
  return 0.299 * ((n >> 16) & 0xff) + 0.587 * ((n >> 8) & 0xff) + 0.114 * (n & 0xff) > 150 ? "#1f2937" : "#f8fafc";
}

/**
 * Mobile-only calendar block look. Mirrors the create sheet's wash: the block
 * background is the entry's picked color — a **custom color** (solid), else the
 * **topic color(s)** (solid for one, a horizontal gradient for several, like the
 * sheet's top band), else the **calendar color**, else **slate for events** so
 * they never render as a bare white card. Plain colorless tasks stay unfilled
 * (light `bg-card`). Title/meta text is readable ink on the fill; priority is a
 * colored border + outward glow. Desktop keeps `washStyle`/`.task-wash`.
 */
export function mobileBlockStyle(input: MobileBlockInput): MobileBlockStyle {
  const topics = input.topicColors.filter(Boolean);
  const topColors = topics.map((c) => pastel(c, SOFT_TOPIC));
  const topBand = topColors.length >= 2 ? `linear-gradient(90deg, ${topColors.join(", ")})` : topColors[0];

  // The background + a representative solid color (for text contrast).
  let background: string | undefined;
  let rep: string | null = null;
  const bodySource = input.customColor ?? input.calendarColor ?? (input.isEvent ? EVENT_DEFAULT : null);
  const body = bodySource ? pastel(bodySource, SOFT_OTHER) : null;
  if (topBand && body) {
    rep = body;
    background = topColors.length === 1
      ? `linear-gradient(to bottom, ${topColors[0]}, ${body} 72px)`
      : `linear-gradient(to bottom, transparent 0, ${body} 72px), ${topBand}`;
  } else if (topBand) {
    rep = topColors[0]!;
    background = topColors.length === 1
      ? topColors[0]
      : topBand;
  } else if (body) {
    rep = body;
    background = body;
  }

  const container: CSSProperties = {};
  const filled = !!background;
  if (background) container.background = background;
  const pri = input.matrixColor ?? PRIORITY_FLAG_HEX[input.priority];
  if (pri) {
    container.borderColor = pri;
    // Glow bleeds into the surrounding gap (no inset) — empty space, not blocks.
    container.boxShadow = `0 0 0 1px ${pri}, 0 0 9px 1px ${pri}55`;
  }

  const ink = rep ? readable(rep) : NEUTRAL_INK;
  const titleText: CSSProperties = { color: ink };
  const metaColors = { time: ink, location: ink, notes: ink };

  return { container, filled, titleText, metaColors };
}
